import { Typography, message } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components';
import LeadsUploadInput from '~/components/inputs/LeadsUploadInput';
import { validateBulkUpload, importBulkUpload } from '~/api/sales';
import {
  StyledModal,
  ModalContent,
  StatusHeader,
  ErrorMessage,
  SuccessMessage,
  ButtonContainer,
  ImportResultContainer,
  ResultSummary,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
} from './ImportLeadsModal.styles';

interface ImportLeadsModalProps {
  open: boolean;
  onClose: () => void;
  onImportSuccess?: () => void;
}

type UploadStatus = 'idle' | 'validating' | 'validated' | 'importing' | 'success' | 'error';

interface RowResult {
  rowNumber: number;
  status: string;
  message: string;
}

interface ImportResult {
  success: boolean;
  status?: string;
  message?: string;
  fileName?: string;
  totalRecords?: number;
  successCount?: number;
  failedCount?: number;
  rowResults?: RowResult[];
  failedRecordNumbers?: number[];
}

const ImportLeadsModal: React.FC<ImportLeadsModalProps> = ({ open, onClose, onImportSuccess }) => {
  const { t } = useTranslation('sales');
  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const handleFileChange = async (file: File | undefined) => {
    if (!file) {
      setSelectedFile(undefined);
      setUploadStatus('idle');
      setErrorMessage('');
      setSuccessMessage('');
      return;
    }

    setSelectedFile(file);
    setUploadStatus('validating');
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Call validation API with the file directly
      const result = await validateBulkUpload(file);

      if (result.success) {
        setUploadStatus('validated');
        const successMsg = result.message || (result.totalRecords
          ? t('File validated successfully. {{count}} records ready to import.', { count: result.totalRecords })
          : t('File validated successfully'));
        setSuccessMessage(successMsg || '');
        message.success(successMsg);
      } else {
        setUploadStatus('error');
        const errorMsg = result.message || t('Validation failed');
        setErrorMessage(errorMsg || '');
        message.error(errorMsg);
      }
    } catch (error: any) {
      console.error('Validation error:', error);
      setUploadStatus('error');
      setErrorMessage(error?.message || t('Failed to validate file'));
      message.error(t('Failed to validate file'));
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      message.error(t('Please upload and validate a file first'));
      return;
    }

    setUploadStatus('importing');
    setErrorMessage('');
    setSuccessMessage('');
    setImportResult(null);

    try {
      // Call import API with the file
      const result = await importBulkUpload(selectedFile);

      if (result.success) {
        setUploadStatus('success');
        setImportResult(result); // Store the result directly (not result.data)
        onImportSuccess?.();

        // Don't close modal - show the status instead
      } else {
        setUploadStatus('error');
        setErrorMessage(result.message || t('Import failed'));
        message.error(result.message || t('Import failed'));
      }
    } catch (error: any) {
      console.error('Import error:', error);
      setUploadStatus('error');
      setErrorMessage(error?.message || t('Failed to import leads'));
      message.error(t('Failed to import leads'));
    }
  };

  const handleClose = () => {
    setSelectedFile(undefined);
    setUploadStatus('idle');
    setErrorMessage('');
    setSuccessMessage('');
    setImportResult(null);
    onClose();
  };

  const getStatusTitle = () => {
    switch (uploadStatus) {
      case 'validating':
        return t('Validating File...');
      case 'validated':
        return t('File Validated');
      case 'importing':
        return t('Importing Leads...');
      case 'success':
        return t('Import Successful');
      case 'error':
        return t('Upload Failed');
      default:
        return t('Upload File');
    }
  };

  const isImportDisabled = uploadStatus !== 'validated';
  const isUploadDisabled = uploadStatus === 'validating' || uploadStatus === 'importing';

  return (
    <StyledModal open={open} onCancel={handleClose} footer={null} width={600} centered closable>
      <ModalContent>
        <StatusHeader status={uploadStatus}>
          <Typography.Title level={5}>{getStatusTitle()}</Typography.Title>
        </StatusHeader>

        {uploadStatus !== 'success' && (
          <LeadsUploadInput
            accept=".csv"
            disabled={isUploadDisabled}
            onChange={handleFileChange}
            value={selectedFile}
            maxSizeB={10 * 1024 * 1024}
          />
        )}

        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}

        {/* Import Result Display */}
        {uploadStatus === 'success' && importResult && (
          <ImportResultContainer>
            <ResultSummary>
              <SummaryItem>
                <SummaryLabel>{t('Total Records')}:</SummaryLabel>
                <SummaryValue>{importResult.totalRecords || 0}</SummaryValue>
              </SummaryItem>
              <SummaryItem success>
                <SummaryLabel>{t('Successfully Added')}:</SummaryLabel>
                <SummaryValue>{importResult.successCount || 0}</SummaryValue>
              </SummaryItem>
              <SummaryItem error>
                <SummaryLabel>{t('Already Exist / Failed')}:</SummaryLabel>
                <SummaryValue>{importResult.failedCount || 0}</SummaryValue>
              </SummaryItem>
            </ResultSummary>
          </ImportResultContainer>
        )}

        <ButtonContainer>
          {uploadStatus === 'success' ? (
            <Button id="doneButton" onClick={handleClose} type="primary">
              {t('Done')}
            </Button>
          ) : (
            <>
              <Button id="cancelButton" onClick={handleClose} type="default">
                {t('Cancel')}
              </Button>
              <Button
                id="importLeadsButton"
                onClick={handleImport}
                type="primary"
                loading={uploadStatus === 'importing'}
                disabled={isImportDisabled}
              >
                {uploadStatus === 'importing' ? t('Importing...') : t('Import')}
              </Button>
            </>
          )}
        </ButtonContainer>
      </ModalContent>
    </StyledModal>
  );
};

export default ImportLeadsModal;

